'use client';

import Link from 'next/link';
import { Reveal } from '@/components/products/Reveal';
import { ProblemSolutionSection } from '@/components/corporate/ProblemSolutionSection';
import { OwnedProductsSection } from '@/components/corporate/OwnedProductsSection';
import { PriorWorkSection } from '@/components/corporate/PriorWorkSection';
import { ResearchSection } from '@/components/corporate/ResearchSection';
import { RoadmapSection } from '@/components/corporate/RoadmapSection';
import { CorporateDetailsSection } from '@/components/corporate/CorporateDetailsSection';
import { company } from '@/lib/company';
import { positioning } from '@/lib/corporate-content';

/**
 * /about-dilacode — the primary About surface.
 *
 * Introduces the legal entity, products, prior work, engineering focus and
 * roadmap in language that works for customers as well as reviewers. Every
 * shared section on this page also appears on the
 * homepage or /company, sourced from the same content module so the three
 * cannot contradict each other.
 *
 * Design follows DESIGN.md: dark canvas, Six Caps display, mono metadata
 * voice, hairline rules, one accent, no cards-in-a-row, no emoji.
 */
export function AboutDilaCodePageClient() {
  return (
    <div className="self-styled">
      {/* ── Hero ── */}
      <section
        className="relative flex min-h-[86dvh] flex-col justify-center"
        style={{ backgroundColor: '#173835' }}
      >
        <div className="content-padding w-full pb-16 pt-40 md:pb-24 md:pt-48">
          <div className="pv-load">
            <span className="eyebrow">About the company</span>
          </div>

          <h1
            className="font-six-caps pv-load mt-8 max-w-[18ch] uppercase leading-[0.84] text-white"
            style={{ fontSize: 'clamp(3.25rem, 11vw, 10rem)', animationDelay: '80ms' }}
          >
            A Canadian
            <br />
            software company
          </h1>

          <p
            className="pv-load mt-10 max-w-2xl text-base font-light leading-relaxed text-white/70 md:text-xl md:leading-relaxed"
            style={{ animationDelay: '160ms' }}
          >
            {positioning}
          </p>

          {/* Identity line — the fact, immediately, before any argument */}
          <div
            className="pv-load mt-12 flex flex-col gap-3 border-t border-white/15 pt-8 md:flex-row md:items-baseline md:gap-10"
            style={{ animationDelay: '240ms' }}
          >
            <span className="font-mono text-[11px] uppercase tracking-[0.24em] text-white/80">
              {company.legalName}
            </span>
            <span className="font-mono text-[11px] uppercase tracking-[0.24em] text-white/45">
              Incorporated in {company.incorporatedIn}
            </span>
            <span className="font-mono text-[11px] uppercase tracking-[0.24em] text-white/45">
              Corporation no. {company.corporationNumber}
            </span>
          </div>
        </div>
      </section>

      {/* ── Who we are ── */}
      <section
        className="relative z-20 border-b border-white/[0.07]"
        style={{ backgroundColor: 'rgba(10,10,10,0.72)' }}
        aria-labelledby="who-heading"
      >
        <div className="content-padding">
          <div className="grid grid-cols-1 gap-10 py-24 md:py-32 lg:grid-cols-[minmax(0,22rem)_1fr] lg:gap-20">
            <Reveal>
              <span className="eyebrow">Who we are</span>
              <h2
                id="who-heading"
                className="font-six-caps mt-6 uppercase leading-[0.88] text-white"
                style={{ fontSize: 'clamp(2.25rem, 5vw, 4rem)' }}
              >
                The company
                <br />
                in plain terms
              </h2>
            </Reveal>

            <Reveal delay={90}>
              <div className="flex max-w-3xl flex-col gap-6 text-base font-light leading-relaxed text-white/65 md:text-lg md:leading-relaxed">
                <p>
                  {company.legalName} is a corporation registered in {company.incorporatedIn},
                  with its registered office in {company.address.city}, {company.address.region}.
                  It is led by {company.contactName}, a software engineer with experience
                  building and releasing business systems.
                </p>
                <p>
                  The company builds its own software products and develops software
                  for clients. Its products cover customer communication, ERP and
                  hospitality operations.
                </p>
                <p>
                  Client projects help us understand real operating problems. The
                  reusable parts of that work improve our products and shorten future
                  development.
                </p>
                <p className="text-white/50">
                  We publish only company and product information that we can verify.
                  Customer names and commercial figures stay private unless we have
                  permission to share them.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── Provenance: DataCode → DILACODE INC. ── */}
      <section
        className="relative z-20 border-b border-white/[0.07]"
        aria-labelledby="provenance-heading"
      >
        <div className="content-padding">
          <div className="grid grid-cols-1 gap-10 py-24 md:py-32 lg:grid-cols-[minmax(0,22rem)_1fr] lg:gap-20">
            <Reveal>
              <span className="eyebrow">Provenance</span>
              <h2
                id="provenance-heading"
                className="font-six-caps mt-6 uppercase leading-[0.88] text-white"
                style={{ fontSize: 'clamp(2.25rem, 5vw, 4rem)' }}
              >
                DataCode
                <br />
                and DilaCode
              </h2>
            </Reveal>

            <Reveal delay={90}>
              <div className="flex max-w-3xl flex-col gap-6 text-base font-light leading-relaxed text-white/65 md:text-lg md:leading-relaxed">
                <p>
                  Most case studies on this site were produced under the studio name{' '}
                  <span className="text-white">DataCode</span> by the same founder
                  and team. They show the team&apos;s earlier work, not work contracted
                  through the Canadian corporation.
                </p>
                <p>
                  {company.legalName} now owns and develops the products described on
                  this site. Earlier work remains labelled as DataCode work so the
                  history is clear.
                </p>
                <p>
                  Existing case-study URLs have been kept so old links continue to
                  work.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Shared narrative sections — same source as the homepage */}
      <ProblemSolutionSection />
      <OwnedProductsSection />
      <PriorWorkSection />
      <ResearchSection />
      <RoadmapSection />
      <CorporateDetailsSection
        heading="Corporate details"
        eyebrow="Verifiable record"
        id="about-corporate-details"
      />

      {/* ── CTA ── */}
      <Link href="/contact" className="block group" style={{ backgroundColor: '#173835' }}>
        <section
          className="flex cursor-pointer flex-col items-center justify-center"
          style={{ paddingTop: 'clamp(6rem, 12vw, 12rem)', paddingBottom: 'clamp(6rem, 12vw, 12rem)' }}
        >
          <p
            className="text-center uppercase transition-opacity duration-500 group-hover:opacity-80"
            style={{ fontSize: '11px', letterSpacing: '0.25em', color: 'rgba(255,255,255,0.4)', marginBottom: 'clamp(1rem, 2vw, 2rem)' }}
          >
            Questions a page cannot answer
          </p>
          <h2
            className="font-six-caps text-white lowercase transition-transform duration-500 group-hover:scale-[1.02]"
            style={{ fontSize: 'clamp(5rem, 20vw, 18rem)', lineHeight: 0.85 }}
          >
            CONTACT
          </h2>
        </section>
      </Link>
    </div>
  );
}
